import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { doNotCallApi, contactsApi } from "@/api";
import { Search, ChevronLeft, ChevronRight, Plus, X, PhoneOff } from "lucide-react";
import { format } from "date-fns";

export default function DoNotCallList() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [showAddDialog, setShowAddDialog] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["do-not-call", { search, page }],
    queryFn: () => {
      const params: Record<string, string> = { page: String(page), limit: "50" };
      if (search) params.search = search;
      return doNotCallApi.list(params);
    },
  });

  const removeMutation = useMutation({
    mutationFn: (contactId: string) => doNotCallApi.remove(contactId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["do-not-call"] });
    },
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <PhoneOff size={20} className="text-red-500" />
          <h2 className="text-xl font-bold">架電NGリスト</h2>
        </div>
        <button
          onClick={() => setShowAddDialog(true)}
          className="flex items-center gap-1 px-3 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700"
        >
          <Plus size={14} />
          NG登録
        </button>
      </div>

      <div className="flex gap-3 items-center">
        <div className="relative flex-1 min-w-48">
          <Search size={16} className="absolute left-3 top-2.5 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="会社名, 電話番号, 名前で検索..."
            className="w-full pl-9 pr-3 py-2 border rounded-md text-sm"
          />
        </div>
        {data && <span className="text-sm text-slate-500">{data.total}件</span>}
      </div>

      {isLoading ? (
        <p className="text-slate-500">読み込み中...</p>
      ) : (
        <>
          <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left px-4 py-2 font-medium text-slate-600">会社名</th>
                  <th className="text-left px-4 py-2 font-medium text-slate-600">担当者</th>
                  <th className="text-left px-4 py-2 font-medium text-slate-600">電話番号</th>
                  <th className="text-left px-4 py-2 font-medium text-slate-600">理由</th>
                  <th className="text-left px-4 py-2 font-medium text-slate-600">登録日</th>
                  <th className="text-left px-4 py-2 font-medium text-slate-600"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {data?.data.map((c) => (
                  <tr key={c.id} className="hover:bg-slate-50">
                    <td className="px-4 py-2">{c.company || "-"}</td>
                    <td className="px-4 py-2">{[c.lastName, c.firstName].filter(Boolean).join(" ") || "-"}</td>
                    <td className="px-4 py-2 font-mono text-xs">{c.phone || "-"}</td>
                    <td className="px-4 py-2 text-slate-500 text-xs max-w-48 truncate">{c.doNotCallNote || "-"}</td>
                    <td className="px-4 py-2 text-xs text-slate-500">{format(new Date(c.updatedAt), "yyyy/MM/dd")}</td>
                    <td className="px-4 py-2">
                      <button
                        onClick={() => {
                          if (confirm("このコンタクトのNG指定を解除しますか？")) {
                            removeMutation.mutate(c.id);
                          }
                        }}
                        className="text-xs text-slate-400 hover:text-red-600"
                      >
                        解除
                      </button>
                    </td>
                  </tr>
                ))}
                {data?.data.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-slate-400">
                      NGリストにコンタクトがありません
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {data && data.totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-500">
                {(page - 1) * data.limit + 1}-{Math.min(page * data.limit, data.total)} / {data.total}件
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="p-2 border rounded-md hover:bg-slate-50 disabled:opacity-50"
                >
                  <ChevronLeft size={16} />
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(data.totalPages, p + 1))}
                  disabled={page >= data.totalPages}
                  className="p-2 border rounded-md hover:bg-slate-50 disabled:opacity-50"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {showAddDialog && (
        <AddDoNotCallDialog onClose={() => setShowAddDialog(false)} />
      )}
    </div>
  );
}

function AddDoNotCallDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [note, setNote] = useState("");
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null);

  const { data: searchResults } = useQuery({
    queryKey: ["contacts-search", search],
    queryFn: () => contactsApi.list({ search, limit: "10" }),
    enabled: search.length >= 2,
  });

  const addMutation = useMutation({
    mutationFn: () => doNotCallApi.add(selectedContactId!, note || undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["do-not-call"] });
      onClose();
    },
  });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold">架電NG登録</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X size={20} />
          </button>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">コンタクト検索</label>
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setSelectedContactId(null); }}
            placeholder="会社名, 名前, 電話番号..."
            className="w-full px-3 py-2 border rounded-md text-sm"
            autoFocus
          />
        </div>

        {searchResults && search.length >= 2 && !selectedContactId && (
          <div className="border rounded-md max-h-48 overflow-y-auto divide-y">
            {searchResults.data.map((c) => (
              <button
                key={c.id}
                onClick={() => { setSelectedContactId(c.id); setSearch(`${c.company || ""} ${c.lastName || ""}`.trim()); }}
                className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm flex justify-between"
              >
                <span>{c.company || "-"} / {[c.lastName, c.firstName].filter(Boolean).join("") || "-"}</span>
                <span className="text-xs text-slate-400 font-mono">{c.phone || "-"}</span>
              </button>
            ))}
            {searchResults.data.length === 0 && (
              <p className="px-3 py-2 text-sm text-slate-400">該当なし</p>
            )}
          </div>
        )}

        {selectedContactId && (
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">理由メモ（任意）</label>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="例: クレーム対応中、先方より架電不要の依頼"
              className="w-full px-3 py-2 border rounded-md text-sm"
            />
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border rounded-md hover:bg-slate-50">
            キャンセル
          </button>
          <button
            onClick={() => addMutation.mutate()}
            disabled={!selectedContactId || addMutation.isPending}
            className="px-4 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
          >
            {addMutation.isPending ? "登録中..." : "NG登録"}
          </button>
        </div>
      </div>
    </div>
  );
}
